import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { Shield, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [saveCredentials, setSaveCredentials] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState("");
  const [show2FADialog, setShow2FADialog] = useState(false);
  const [twoFactorMethod, setTwoFactorMethod] = useState('TOTP');
  const [timeRemaining, setTimeRemaining] = useState(120);
  const [resendCooldown, setResendCooldown] = useState(0);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const logoUrl = "/icons/Logo_New.png";

  // Redirect if already logged in when component mounts
  // Don't redirect during active login flow (when loading is true)
  React.useEffect(() => {
    if (user && !loading) {
      switch (user.role) {
        case "STAFF":
          navigate("/staff/dashboard", { replace: true });
          break;
        case "SYSTEM_OWNER":
          navigate("/system", { replace: true });
          break;
        case "ORG_OWNER":
          navigate("/merchant", { replace: true });
          break;
        case "EMPLOYEE":
          navigate("/redeemer", { replace: true });
          break;
        case "AGENCY":
          navigate("/agency", { replace: true });
          break;
        case "REDEEMER":
          navigate("/redeemer", { replace: true });
          break;
        default:
          navigate("/merchant", { replace: true });
      }
    }
  }, [user, loading, navigate]);
  
  // Countdown timer for 2FA
  React.useEffect(() => {
    if (show2FADialog && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [show2FADialog, timeRemaining]);

  // Cooldown timer for resend button (EMAIL 2FA)
  React.useEffect(() => {
    if (show2FADialog && twoFactorMethod === 'EMAIL' && resendCooldown > 0) {
      const timer = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [show2FADialog, twoFactorMethod, resendCooldown]);

  // Load saved credentials on mount
  React.useEffect(() => {
    try {
      const savedEmail = localStorage.getItem('user_remember_email');
      const savedPassword = localStorage.getItem('user_remember_password');
      const shouldSave = localStorage.getItem('user_save_credentials') === 'true';
      
      if (savedEmail && shouldSave) {
        setEmail(savedEmail);
        setSaveCredentials(true);
        
        // Simple base64 decode
        if (savedPassword) {
          try {
            setPassword(atob(savedPassword));
          } catch (e) {
            // Invalid base64, ignore
          }
        }
      }
    } catch (e) {
      console.error('Error loading saved credentials:', e);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!email.trim()) {
      setFormError(t('login.emailRequired') || 'Bitte gib deine E-Mail-Adresse ein.');
      return;
    }
    if (!password) {
      setFormError(t('login.passwordRequired') || 'Bitte gib dein Passwort ein.');
      return;
    }
    setLoading(true);
    try {
      // Save credentials if "save credentials" is checked
      if (saveCredentials) {
        localStorage.setItem('user_remember_email', email);
        localStorage.setItem('user_remember_password', btoa(password));
        localStorage.setItem('user_save_credentials', 'true');
      } else {
        localStorage.removeItem('user_remember_email');
        localStorage.removeItem('user_remember_password');
        localStorage.removeItem('user_save_credentials');
      }
      
      const result = await login(email, password, rememberMe);
      
      // Check if 2FA is required (staff users)
      if (result.requiresTwoFactor) {
        const m = result.method || 'TOTP';
        setTwoFactorMethod(m);
        setShow2FADialog(true);
        setTimeRemaining(m === 'EMAIL' ? 600 : 120);
        setResendCooldown(0);
        setLoading(false);
        return;
      }
      
      // Navigation is handled by useEffect when user state changes
      // No manual navigation needed here
    } catch (error) {
          if (error?.status === 429 || error?.payload?.code === 'STAFF_LOGIN_RATE_LIMIT') {
            toast({
              title: t('login.tooManyAttempts.title'),
              description: t('login.tooManyAttempts.desc'),
              variant: "destructive",
            });
          } else if (error?.payload?.code === 'TWO_FA_REQUIRED') {
            toast({
              title: t('login.twoFaRequired'),
              variant: "destructive",
            });
          } else {
            toast({
              title: t('login.failed.title'),
              description: error.message || t('login.failed.desc'),
              variant: "destructive",
            });
          }
    } finally {
      setLoading(false);
    }
  };
  
  const handle2FASubmit = async () => {
    if (!twoFactorToken || twoFactorToken.length !== 6) {
      toast({
        title: t('common.invalidCode'),
        description: "Bitte geben Sie einen 6-stelligen Code ein",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      const result = await login(email, password, rememberMe, twoFactorToken);
      
      toast({
        title: t('login.success'),
      });
      
      setShow2FADialog(false);
      setLoading(false);
      
      // Navigation is handled by useEffect above when user state changes
      // No need to navigate manually here to avoid double navigation
    } catch (err) {
      console.error('[LoginPage] 2FA error:', err);
      toast({
        title: t('login.twoFaFailed.title'),
        description: err.message || t('login.twoFaFailed.desc'),
        variant: "destructive",
      });
      setShow2FADialog(false);
      setLoading(false);
    }
  };

  const handleResendEmailCode = async () => {
    if (twoFactorMethod !== 'EMAIL') return;
    if (resendCooldown > 0) return;
    try {
      setLoading(true);
      const result = await login(email, password, rememberMe);
      if (result.requiresTwoFactor && (result.method || 'EMAIL') === 'EMAIL') {
        setTimeRemaining(600);
        setResendCooldown(120);
        toast({ title: t('login.codeResent') });
      } else {
        // Falls der Login direkt durchging (z. B. Timing), Dialog schließen
        setShow2FADialog(false);
      }
    } catch (e) {
      toast({ title: t('login.sendError'), description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel2FA = () => {
    setShow2FADialog(false);
    setTwoFactorToken("");
    setTimeRemaining(120);
    setLoading(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="shadow-xl w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img src={logoUrl} alt="Scanmirein Logo" className="h-20" />
          </div>
          <CardTitle className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {t('login.welcome')}
          </CardTitle>
          <CardDescription>{t('login.signInToContinue')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="email">{t('login.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('login.emailPlaceholder')}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('login.password')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('login.passwordPlaceholder')}
                required
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  id="rememberMe"
                  type="checkbox"
                  className="h-4 w-4"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <Label htmlFor="rememberMe" className="text-sm cursor-pointer">{t('login.staySignedIn')}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  id="saveCredentials"
                  type="checkbox"
                  className="h-4 w-4"
                  checked={saveCredentials}
                  onChange={(e) => setSaveCredentials(e.target.checked)}
                />
                <Label htmlFor="saveCredentials" className="text-sm cursor-pointer">{t('login.rememberCredentials')}</Label>
              </div>
            </div>
            {formError && (
              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {formError}
              </div>
            )}
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              disabled={loading}
            >
              {loading ? t('login.signingIn') : t('login.signIn')}
            </Button>
            <div className="text-center text-xs text-muted-foreground space-y-1">
              <div>
                {t('login.activateLicense')} {" "}
                <Link to="/activate" className="underline">
                  {t('login.here')}
                </Link>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
      
      {/* 2FA Dialog */}
      <Dialog open={show2FADialog} onOpenChange={setShow2FADialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              {t('login.twoFaTitle')}
            </DialogTitle>
            <DialogDescription>
              {twoFactorMethod === 'EMAIL'
                ? t('login.twoFaEmailDesc')
                : t('login.twoFaTotpDesc')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>{t('login.timeRemaining', { time: formatTime(timeRemaining) })}</span>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="twoFactorToken">{t('login.authCode')}</Label>
              <Input
                id="twoFactorToken"
                type="text"
                maxLength={6}
                value={twoFactorToken}
                onChange={(e) => setTwoFactorToken(e.target.value.replace(/\D/g, ''))}
                placeholder={t('login.codePlaceholder')}
                className="text-center text-2xl tracking-widest"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && twoFactorToken.length === 6) {
                    handle2FASubmit();
                  }
                }}
              />
              {twoFactorMethod === 'EMAIL' && (
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    {timeRemaining > 0 ? t('login.codeValidFor', { time: formatTime(timeRemaining) }) : t('login.codeExpired')}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleResendEmailCode}
                    disabled={loading || resendCooldown > 0}
                    className="h-6 px-2"
                  >
                    {resendCooldown > 0 ? t('login.resendIn', { time: formatTime(resendCooldown) }) : t('login.resendCode')}
                  </Button>
                </div>
              )}
              {twoFactorMethod === 'EMAIL' && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {t('login.noCodeHelp')}
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel2FA}
              disabled={loading}
            >
              {t('login.cancel')}
            </Button>
            <Button
              type="button"
              onClick={handle2FASubmit}
              disabled={loading || twoFactorToken.length !== 6}
            >
              {loading ? t('login.verifying') : t('login.verify')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
